import { NavLink } from "react-router-dom";
import {
  BarChart3,
  Inbox,
  LayoutDashboard,
  PlusCircle,
  ShieldCheck,
  Users,
} from "lucide-react";
import blinkLogo from "@/assets/blink-logo-white.svg";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const mainNavItems = [
  { to: "/my-requests", label: "My Requests", icon: Inbox },
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    viewerOnly: true, // visible to reviewers AND read-only roles
  },
  {
    to: "/pm-summary",
    label: "Summary",
    icon: BarChart3,
    reviewerOnly: true, // reviewers only — not shown to read-only roles
  },
];

export function Sidebar() {
  const { isReviewer, isAdmin, isReadOnly, canSubmit, canViewDashboard } =
    useAuth();

  return (
    <aside className="flex w-60 flex-col border-r bg-card">
      {/* Brand */}
      <div className="flex h-16 items-center gap-2 border-b px-4">
        <div className="flex h-8 w-12 items-center justify-center rounded-md bg-primary">
          <img src={blinkLogo} alt="Blink" className="h-8 w-12" />
        </div>
        <div className="leading-tight">
          <p className="text-sm font-bold text-foreground">Relay</p>
          <p className="text-xs text-muted-foreground">Tech Request Portal</p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3">
        {/* New Request — hidden for pure read-only users */}
        {canSubmit && (
          <NavLink
            to="/submit"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition-colors mb-3",
                isActive
                  ? "bg-primary/90 text-primary-foreground"
                  : "bg-primary text-primary-foreground hover:bg-primary/90",
              )
            }
          >
            <PlusCircle className="h-4 w-4 shrink-0" />
            New Request
          </NavLink>
        )}

        {/* Section label */}
        <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
          Navigation
        </p>

        <div className="space-y-0.5">
          {mainNavItems.map(
            ({ to, label, icon: Icon, reviewerOnly, viewerOnly }) => {
              if (reviewerOnly && !isReviewer) return null;
              if (viewerOnly && !canViewDashboard) return null;
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </NavLink>
              );
            },
          )}
        </div>
      </nav>

      {/* Admin section */}
      {isAdmin && (
        <div className="border-t p-3">
          <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">
            Admin
          </p>
          <div className="space-y-0.5">
            {[
              { to: "/admin/users", label: "User Management", icon: Users },
              {
                to: "/admin/health",
                label: "System Health",
                icon: ShieldCheck,
              },
            ].map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t p-3 space-y-1.5">
        {isReadOnly && (
          <div className="mx-3 rounded-md bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-2 py-1 text-xs text-amber-700 dark:text-amber-300 font-medium">
            Read-only access
          </div>
        )}
        <p className="px-3 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Blink Network
        </p>
      </div>
    </aside>
  );
}
